import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getTest, getCandidates, addCandidate, importCandidatesCSV, deleteCandidate, markInvited, bulkInvite, exportCandidatesCSV } from "@/lib/api";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Brain, ArrowLeft, Plus, Upload, Download, Send, Trash2, Loader2, Users, Copy, Check, Link as LinkIcon, FileText, CheckCircle, XCircle, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

export default function AdminTestCandidatesPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);
  const fileInputRef = useRef(null);

  // Add form
  const [addForm, setAddForm] = useState({ name: "", email: "", cv_summary: "", phone: "" });
  const [addSaving, setAddSaving] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [csvImporting, setCsvImporting] = useState(false);

  // CSV export filters (Phase 6 enhancement)
  const [exportFilters, setExportFilters] = useState({ status: "", workflow_status: "", start_date: "", end_date: "" });
  const [exportOpen, setExportOpen] = useState(false);

  const activeFilterCount =
    (exportFilters.status ? 1 : 0) +
    (exportFilters.workflow_status ? 1 : 0) +
    (exportFilters.start_date ? 1 : 0) +
    (exportFilters.end_date ? 1 : 0);

  const handleExport = () => {
    window.open(exportCandidatesCSV(testId, exportFilters), "_blank");
    setExportOpen(false);
  };

  const handleExportReset = () => setExportFilters({ status: "", workflow_status: "", start_date: "", end_date: "" });

  useEffect(() => { loadData(); }, [testId]);

  const loadData = async () => {
    try {
      const [testRes, candRes] = await Promise.all([getTest(testId), getCandidates(testId)]);
      setTest(testRes.data);
      setCandidates(candRes.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!addForm.name.trim() || !addForm.email.trim()) { toast.error("Name and email required"); return; }
    setAddSaving(true);
    try {
      await addCandidate(testId, addForm);
      toast.success("Candidate added");
      setAddDialogOpen(false);
      setAddForm({ name: "", email: "", cv_summary: "", phone: "" });
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
    finally { setAddSaving(false); }
  };

  const handleCSVImport = async () => {
    if (!csvText.trim()) { toast.error("Paste CSV data"); return; }
    setCsvImporting(true);
    try {
      const res = await importCandidatesCSV(testId, csvText);
      toast.success(`Imported ${res.data.imported} candidates${res.data.skipped > 0 ? ` (${res.data.skipped} skipped)` : ''}`);
      setCsvDialogOpen(false);
      setCsvText("");
      loadData();
    } catch (e) { toast.error("Failed to import CSV"); }
    finally { setCsvImporting(false); }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCsvText(ev.target.result);
      setCsvDialogOpen(true);
    };
    reader.readAsText(file);
  };

  const handleDelete = async (id) => {
    try { await deleteCandidate(id); toast.success("Removed"); loadData(); }
    catch (e) { toast.error("Failed to remove"); }
  };

  const getInviteLink = (token) => {
    return `${window.location.origin}/take-test/${token}`;
  };

  const copyLink = (id, token) => {
    navigator.clipboard.writeText(getInviteLink(token));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Link copied!");
  };

  const handleMarkInvited = async (id) => {
    try { await markInvited(id); loadData(); }
    catch (e) { toast.error("Failed"); }
  };

  const handleBulkInvite = async () => {
    if (selected.size === 0) { toast.error("Select candidates first"); return; }
    try {
      await bulkInvite(testId, Array.from(selected));
      toast.success(`${selected.size} candidates marked as invited`);
      setSelected(new Set());
      loadData();
    } catch (e) { toast.error("Failed"); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) setSelected(new Set());
    else setSelected(new Set(candidates.map(c => c.id)));
  };

  const openDetailDialog = (candidate) => {
    setSelectedCandidate(candidate);
    setDetailDialogOpen(true);
  };

  const statusColors = {
    pending: "bg-secondary text-muted-foreground",
    invited: "bg-[hsl(var(--chart-1))]/15 text-[hsl(var(--chart-1))] border border-[hsl(var(--chart-1))]/25",
    started: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/25",
    completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border border-[hsl(var(--success))]/25",
  };

  if (loading) {
    return (<div className="min-h-screen bg-background flex"><AdminSideNav active="Tests" /><div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Tests" />
      <div className="flex-1 flex flex-col">
        <header className="px-4 sm:px-6 lg:px-8 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/tests')}><ArrowLeft className="w-4 h-4" /></Button>
            <img 
              src="/branding/securado-logo-white.png" 
              alt="Securado" 
              className="h-6 w-auto object-contain"
            />
            <div>
              <span className="font-display font-semibold text-sm">{test?.title}</span>
              <span className="text-xs text-muted-foreground ml-2">Candidates</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Popover open={exportOpen} onOpenChange={setExportOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid="download-csv-button">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download CSV
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[10px]">{activeFilterCount}</Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <SlidersHorizontal className="w-3.5 h-3.5" /> Export filters
                  </div>
                  <div>
                    <Label className="text-xs mb-1.5 block">Status</Label>
                    <Select
                      value={exportFilters.status || "all"}
                      onValueChange={(v) => setExportFilters({ ...exportFilters, status: v === "all" ? "" : v })}
                    >
                      <SelectTrigger className="h-9" data-testid="csv-filter-status-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="invited">Invited</SelectItem>
                        <SelectItem value="started">Started</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs mb-1.5 block">Workflow stage</Label>
                    <Select
                      value={exportFilters.workflow_status || "all"}
                      onValueChange={(v) => setExportFilters({ ...exportFilters, workflow_status: v === "all" ? "" : v })}
                    >
                      <SelectTrigger className="h-9" data-testid="csv-filter-workflow-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All stages</SelectItem>
                        <SelectItem value="applied">Applied</SelectItem>
                        <SelectItem value="test_invited">Test invited</SelectItem>
                        <SelectItem value="test_completed">Test completed</SelectItem>
                        <SelectItem value="ai_recommended">AI recommended</SelectItem>
                        <SelectItem value="shortlisted">Shortlisted</SelectItem>
                        <SelectItem value="panel_review">Panel review</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="hired">Hired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="csv-from" className="text-xs mb-1.5 block">From</Label>
                      <Input
                        id="csv-from"
                        type="date"
                        value={exportFilters.start_date}
                        onChange={(e) => setExportFilters({ ...exportFilters, start_date: e.target.value })}
                        className="h-9"
                        data-testid="csv-filter-start-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="csv-to" className="text-xs mb-1.5 block">To</Label>
                      <Input
                        id="csv-to"
                        type="date"
                        value={exportFilters.end_date}
                        onChange={(e) => setExportFilters({ ...exportFilters, end_date: e.target.value })}
                        className="h-9"
                        data-testid="csv-filter-end-date"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button variant="ghost" size="sm" onClick={handleExportReset} className="flex-1" data-testid="csv-filter-reset">
                      Reset
                    </Button>
                    <Button size="sm" onClick={handleExport} className="flex-1" data-testid="csv-filter-download">
                      <Download className="w-3.5 h-3.5 mr-1" /> Download
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="outline" size="sm" onClick={() => setCsvDialogOpen(true)}>
              <Upload className="w-3.5 h-3.5 mr-1" /> Import CSV
            </Button>
            <input type="file" ref={fileInputRef} accept=".csv" className="hidden" onChange={handleFileUpload} />
            <Button size="sm" onClick={() => setAddDialogOpen(true)} className="btn-press" data-testid="add-candidate-button">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Candidate
            </Button>
          </div>
        </header>

        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto">
            {/* Bulk Actions */}
            {selected.size > 0 && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
                <span className="text-sm">{selected.size} selected</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleBulkInvite}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Mark as Invited
                  </Button>
                </div>
              </div>
            )}

            {candidates.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No candidates yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add candidates manually or import a CSV file</p>
                <div className="flex gap-2 justify-center mt-4">
                  <Button onClick={() => setAddDialogOpen(true)} variant="secondary"><Plus className="w-4 h-4 mr-2" /> Add Manually</Button>
                  <Button onClick={() => setCsvDialogOpen(true)} variant="secondary"><Upload className="w-4 h-4 mr-2" /> Import CSV</Button>
                </div>
              </div>
            ) : (
              <div className="border border-border rounded-xl overflow-hidden" data-testid="candidates-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30">
                      <th className="w-10 px-4 py-3">
                        <input type="checkbox" checked={selected.size === candidates.length && candidates.length > 0} onChange={toggleAll} className="rounded" />
                      </th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Candidate</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden md:table-cell">CV Summary</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Score</th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map(c => (
                      <tr key={c.id} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors">
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSelect(c.id)} className="rounded" />
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.email}</p>
                          {c.phone && <p className="text-xs text-muted-foreground">{c.phone}</p>}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <p className="text-xs text-muted-foreground line-clamp-2 max-w-xs">{c.cv_summary || '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-xs ${statusColors[c.status] || ''}`}>{c.status}</Badge>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {c.status === 'completed' ? (
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-mono font-semibold ${c.passed ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--destructive))]'}`}>
                                {c.score_pct}%
                              </span>
                              {c.passed ? <CheckCircle className="w-3.5 h-3.5 text-[hsl(var(--success))]" /> : <XCircle className="w-3.5 h-3.5 text-[hsl(var(--destructive))]" />}
                              <span className="text-xs text-muted-foreground">{c.correct_answers}/{c.total_questions}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {c.status === 'completed' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="View details" onClick={() => openDetailDialog(c)} data-testid={`view-details-${c.id}`}>
                                <FileText className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Copy invite link" onClick={() => copyLink(c.id, c.invite_token)}>
                              {copiedId === c.id ? <Check className="w-3.5 h-3.5 text-[hsl(var(--success))]" /> : <Copy className="w-3.5 h-3.5" />}
                            </Button>
                            {c.status === 'pending' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8" title="Mark as invited" onClick={() => handleMarkInvited(c.id)}>
                                <Send className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" onClick={() => handleDelete(c.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Candidate Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader><DialogTitle className="font-display">Add Candidate</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Name *</label>
              <Input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} placeholder="John Doe" className="bg-card" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Email *</label>
              <Input value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} placeholder="john@example.com" className="bg-card" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Phone</label>
              <Input value={addForm.phone} onChange={e => setAddForm({ ...addForm, phone: e.target.value })} placeholder="+1 234 567 8900" className="bg-card" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">CV Summary</label>
              <textarea value={addForm.cv_summary} onChange={e => setAddForm({ ...addForm, cv_summary: e.target.value })} placeholder="Brief background..." rows={3} className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={addSaving} className="btn-press" data-testid="add-candidate-submit">
              {addSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onOpenChange={setCsvDialogOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader><DialogTitle className="font-display">Import Candidates from CSV</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Paste CSV data or upload a file. Expected columns: <code className="text-xs bg-secondary px-1 py-0.5 rounded">name,email,cv_summary,phone</code></p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Upload CSV File
              </Button>
            </div>
            <textarea
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder={`name,email,cv_summary,phone\nJohn Doe,john@example.com,8 years in sales,+1234567890\nJane Smith,jane@example.com,5 years in tech,`}
              rows={8}
              className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none"
              data-testid="csv-import-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCsvDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCSVImport} disabled={csvImporting} className="btn-press" data-testid="csv-import-submit">
              {csvImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Candidate Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl bg-card border-border max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Candidate Details</DialogTitle>
          </DialogHeader>
          {selectedCandidate && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Name:</span>
                    <p className="font-medium">{selectedCandidate.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">{selectedCandidate.email}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <p className="font-medium">{selectedCandidate.phone || '—'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span>
                    <Badge variant="outline" className={`text-xs ml-2 ${statusColors[selectedCandidate.status] || ''}`}>
                      {selectedCandidate.status}
                    </Badge>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Test Score */}
              {selectedCandidate.status === 'completed' && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Test Performance</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <Card className="border-border bg-card/50">
                        <CardContent className="p-4 text-center">
                          <p className="text-2xl font-display font-semibold">{selectedCandidate.score_pct}%</p>
                          <p className="text-xs text-muted-foreground mt-1">Score</p>
                        </CardContent>
                      </Card>
                      <Card className="border-border bg-card/50">
                        <CardContent className="p-4 text-center">
                          <p className="text-2xl font-display font-semibold">{selectedCandidate.correct_answers}/{selectedCandidate.total_questions}</p>
                          <p className="text-xs text-muted-foreground mt-1">Correct</p>
                        </CardContent>
                      </Card>
                      <Card className="border-border bg-card/50">
                        <CardContent className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {selectedCandidate.passed ? 
                              <CheckCircle className="w-6 h-6 text-[hsl(var(--success))]" /> : 
                              <XCircle className="w-6 h-6 text-[hsl(var(--destructive))]" />
                            }
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{selectedCandidate.passed ? 'Passed' : 'Failed'}</p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  <Separator />
                </>
              )}

              {/* Pre-Screening Answers */}
              {selectedCandidate.pre_screening_answers && Object.keys(selectedCandidate.pre_screening_answers).length > 0 && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Pre-Screening Responses</h3>
                    <div className="space-y-3">
                      {Object.entries(selectedCandidate.pre_screening_answers).map(([question, answer]) => (
                        <div key={question} className="bg-secondary/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-1">{question}</p>
                          <p className="text-sm">{answer || '—'}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />
                </>
              )}

              {/* Proctoring Summary */}
              {selectedCandidate.proctoring_summary && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Proctoring Summary</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="bg-secondary/30 rounded-lg p-3 text-center">
                        <p className="text-lg font-semibold">{selectedCandidate.proctoring_summary.tab_switches || 0}</p>
                        <p className="text-xs text-muted-foreground">Tab Switches</p>
                      </div>
                      <div className="bg-secondary/30 rounded-lg p-3 text-center">
                        <p className="text-lg font-semibold">{selectedCandidate.proctoring_summary.fullscreen_exits || 0}</p>
                        <p className="text-xs text-muted-foreground">Fullscreen Exits</p>
                      </div>
                      <div className="bg-secondary/30 rounded-lg p-3 text-center">
                        <p className="text-lg font-semibold">{selectedCandidate.proctoring_summary.clipboard_events || 0}</p>
                        <p className="text-xs text-muted-foreground">Clipboard Events</p>
                      </div>
                      <div className="bg-secondary/30 rounded-lg p-3 text-center">
                        <p className="text-lg font-semibold">{selectedCandidate.proctoring_summary.camera_issues || 0}</p>
                        <p className="text-xs text-muted-foreground">Camera Issues</p>
                      </div>
                      <div className="bg-secondary/30 rounded-lg p-3 text-center">
                        <p className="text-lg font-semibold">{selectedCandidate.proctoring_summary.mic_issues || 0}</p>
                        <p className="text-xs text-muted-foreground">Mic Issues</p>
                      </div>
                    </div>
                  </div>

                  <Separator />
                </>
              )}

              {/* Anomaly Logs */}
              {selectedCandidate.anomaly_logs && selectedCandidate.anomaly_logs.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3">Anomaly Timeline</h3>
                  <div className="space-y-2 max-h-[300px] overflow-auto">
                    {selectedCandidate.anomaly_logs.map((log, idx) => (
                      <div key={`${log.timestamp || ""}-${log.event_type}-${idx}`} className="flex items-start gap-3 text-sm border-l-2 border-border pl-3 py-1">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          log.severity === 'high' ? 'bg-[hsl(var(--destructive))]' :
                          log.severity === 'medium' ? 'bg-[hsl(var(--warning))]' :
                          'bg-[hsl(var(--info))]'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{log.event_type.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">{log.details}</p>
                          <p className="text-xs text-muted-foreground font-mono mt-0.5">
                            {new Date(log.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
